import { useState, useCallback, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";

/* Custom "Reply" icon from Figma — node 139-6636 */
const CustomReplyIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 20 20">
        <path
            d="M3.74992 10.4999L3.74982 3.75L5.24982 3.74998L5.24989 8.99993L12.8788 9L9.9165 6.03768L10.9772 4.97702L15.7501 9.75L10.9772 14.523L9.9165 13.4623L12.8788 10.5L3.74992 10.4999Z"
            fill="currentColor"
        />
    </SvgIcon>
);

/* Custom "Send" icon from Figma — node 139-6639 */
const CustomSendIcon = (props) => (
    <SvgIcon {...props} viewBox="0 0 20 20">
        <path
            d="M2.91667 1.12132C2.98688 1.12132 3.05595 1.13906 3.11747 1.17289L18.5028 9.63483C18.7045 9.74575 18.7781 9.99908 18.6672 10.2007C18.6291 10.27 18.5721 10.327 18.5028 10.3651L3.11747 18.827C2.91583 18.9379 2.66247 18.8643 2.55157 18.6628C2.51774 18.6012 2.5 18.5322 2.5 18.4619V1.53798C2.5 1.30787 2.68655 1.12132 2.91667 1.12132ZM4.16667 3.65207V9.16658H8.33333V10.8332H4.16667V16.3478L15.7083 9.99992L4.16667 3.65207Z"
            fill="currentColor"
        />
    </SvgIcon>
);

/**
 * "Continue conversation" input — Figma node 687:4403.
 */
export default function ContinueConversationInput({
    onSubmit,
    disabled = false,
    focusToken = 0,
}) {
    const [value, setValue] = useState("");
    const inputRef = useRef(null);

    const handleSend = useCallback(() => {
        const trimmed = value.trim();
        if (!trimmed || disabled) return;
        onSubmit(trimmed);
        setValue("");
    }, [value, disabled, onSubmit]);

    useEffect(() => {
        if (disabled) return;
        const rafId = requestAnimationFrame(() => {
            const input = inputRef.current;
            if (!input) return;
            input.focus();
            const cursorPosition = input.value.length;
            if (typeof input.setSelectionRange === "function") {
                input.setSelectionRange(cursorPosition, cursorPosition);
            }
        });
        return () => cancelAnimationFrame(rafId);
    }, [disabled, focusToken]);

    return (
        <Box sx={{ mt: 0 }}>
            <TextField
                fullWidth
                inputRef={inputRef}
                placeholder="Continue conversation"
                value={value}
                disabled={disabled}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                    }
                }}
                multiline
                minRows={1}
                maxRows={4}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <CustomReplyIcon
                                sx={{
                                    fontSize: 18,
                                    color: "#898989",
                                }}
                            />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleSend}
                                disabled={disabled || !value.trim()}
                                sx={{
                                    width: 20,
                                    height: 20,
                                    p: 0,
                                    color: "#898989",
                                    "&:hover": { color: "#3d3a37" },
                                    "&:disabled": { color: "#D1D5DB" },
                                }}
                            >
                                <CustomSendIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </InputAdornment>
                    ),
                    sx: {
                        width: "100%",
                        minHeight: 56,
                        borderRadius: "12px",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 16,
                        lineHeight: "24px",
                        color: "#3d3a37",
                        backgroundColor: "#fff",
                        gap: "16px",
                        px: "24px",
                        py: "16px",
                        boxShadow: "0 0 0 1px rgba(0,0,0,0.01), 0 0 0 3px rgba(0,0,0,0.05)",
                        "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": {
                            p: 0,
                            height: "auto",
                            lineHeight: "24px",
                        },
                        "& .MuiInputAdornment-root": {
                            m: 0,
                        },

                        "& fieldset": {
                            borderColor: "#A6A6A6",
                        },
                        "&:hover fieldset": {
                            borderColor: "#8F8F8F !important",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#7A7A7A !important",
                            borderWidth: "1px !important",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#f9f9f9",
                        },
                        "& input::placeholder, & textarea::placeholder": {
                            color: "#898989",
                            fontSize: 16,
                            fontWeight: 400,
                            opacity: 1,
                        },
                    },
                }}
            />
        </Box>
    );
}
